import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import { ModuleDispatcher } from '$partials/modules/index.js';
import { Button } from '$partials/ui/index.js';

import {
    Footer,
    Header,
    Meta,
    StickyCTAButtons
} from '$partials/global/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const Home = (props) => {
    // references
    const watchedElemRef = useRef();

    // state
    const [ headerHeight, setHeaderHeight ] = useState();

    // misc
    const page = props?.data?.page;

    const hasNewsletterModule = page?.modules?.find((item) => item.__typename === 'NewsletterBlock');

    const modulesArray = page?.modules;

    function getAllIndexes(arr, val) {
        let indexes = [], i;
        for(i = 0; i < arr.length; i++) {
            if (arr[i].__typename === val) {
                indexes.push(i);
            }
        }
        return indexes;
    }

    const accordionIndexes = getAllIndexes(modulesArray, 'AccordionBlock');
    const imageIndexes = getAllIndexes(modulesArray, 'FullWidthMediaBlock');
    const featureIndexes = getAllIndexes(modulesArray, 'PrimaryFeatureBlock');
    const combinedIndexes = imageIndexes.concat(featureIndexes);
    const indexesBefore = combinedIndexes.map((item) => {
        return item-1;
    });
    const lastIndex = modulesArray.length-1;
    const targetIndexes = imageIndexes.concat(indexesBefore).concat(accordionIndexes).concat(lastIndex);

    return (
        <>
            <Meta
                description={page?.searchDescription || page?.listingText}
                domain={props.domain}
                image={page?.listingImage}
                page={page}
                title='Public Practice' />

            {/* Navigation */}
            <Header
                {...props?.data?.navigation}
                {...props?.data?.ticker}
                headerHeight={(height) => setHeaderHeight(height)}
                pageType={page?.__typename}
                watchedElemRef={watchedElemRef} />
            <StickyCTAButtons
                cta1={page?.stickyCta1?.[0]}
                cta2={page?.stickyCta2?.[0]} />

            <main
                className='bg-white relative'
                id='main-content'
                ref={watchedElemRef}
                style={{marginTop: `${headerHeight}px`}}>

                {/* Standfirst */}
                <div className='layout-container relative bg-white md:flex border-t border-t-charcoal'>
                    {/* Spacer for desktop */}
                    <div className='md:w-1/12' />
                    <div className='md:w-11/12'>
                        <div className='md:pt-100 md:pb-80 standfirst-height relative sm:w-8/12 md:w-7/11'>
                            <div className='absolute bottom-20 md:relative'>
                                <h1 className='type-style-1'>
                                    {page?.standfirst}
                                </h1>

                                {(page?.standfirstCtaLabel && page?.standfirstCtaLink) && (
                                    <p className='mt-20'>
                                        <Button
                                            as='a'
                                            className='pill-button'
                                            href={page.standfirstCtaLink.url}>
                                            {page.standfirstCtaLabel}
                                        </Button>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modules */}
                <div className='bg-white relative'>
                    {page?.modules?.map((module, idx) => {
                        const nextModuleType = page.modules?.[idx + 1]?.__typename;

                        if (
                            module.__typename === 'ArticleCarouselBlock'
                            || module.__typename === 'CtaBlock'
                            || nextModuleType === 'ArticleCarouselBlock'
                            || nextModuleType === 'CtaBlock'
                            || (module.__typename === 'CardDuoBlock' && nextModuleType === 'CardDuoBlock')
                        ) {
                            module.borderBottom = false;
                        } else {
                            if (targetIndexes.includes(idx)) {
                                module.borderBottom = false;
                            } else {
                                module.borderBottom = true;
                            }
                        }

                        return (
                            <ModuleDispatcher
                                index={idx}
                                key={idx}
                                {...module} />
                        );
                    })}
                </div>
            </main>

            <Footer
                hasNewsletterModule={hasNewsletterModule}
                {...props?.data?.footer}
                {...props?.data?.newsletter} />
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        footer: PropTypes.object,
        page: PropTypes.object.isRequired,
        navigation: PropTypes.object,
        newsletter: PropTypes.object,
        ticker: PropTypes.object,
    }),
    domain: PropTypes.string,
};

Home.propTypes = propTypes;

export default Home;
